/**
 * Utility function to format content with proper structure and styling
 * Used for displaying CMS content with insurance-specific formatting
 */
export const formatContentWithStructure = (content: string): string => {
  if (!content) return "";

  // Add CSS styles for better formatting
  const styles = `
    <style>
      .content-section {
        margin-bottom: 24px;
      }
      .content-section h3 {
        color: #20a7db;
        font-size: 18px;
        font-weight: 600;
        margin-bottom: 12px;
        margin-top: 20px;
        border-bottom: 2px solid #e3f2fd;
        padding-bottom: 8px;
      }
      .content-section h4 {
        color: #424242;
        font-size: 16px;
        font-weight: 600;
        margin-bottom: 8px;
        margin-top: 16px;
      }
      .content-section p {
        margin-bottom: 12px;
        line-height: 1.6;
        color: #333;
      }
      .content-section ul {
        margin-bottom: 16px;
        padding-left: 20px;
      }
      .content-section li {
        margin-bottom: 8px;
        line-height: 1.5;
      }
      .content-section strong {
        color: #20a7db;
        font-weight: 600;
      }
      .content-section .highlight-box {
        background: #f5f5f5;
        border-left: 4px solid #20a7db;
        padding: 16px;
        margin: 16px 0;
        border-radius: 4px;
      }
      .content-section .example-box {
        background: #e8f5e8;
        border: 1px solid #1c9d8a;
        padding: 16px;
        margin: 16px 0;
        border-radius: 4px;
      }
      .content-section .warning-box {
        background: #fff3e0;
        border: 1px solid #FFAC1C;
        padding: 16px;
        margin: 16px 0;
        border-radius: 4px;
      }
      .content-section .recommendation-box {
        background: #e3f2fd;
        border: 1px solid #20a7db;
        padding: 16px;
        margin: 16px 0;
        border-radius: 4px;
      }
    </style>
  `;

  // Process the content to add structure
  let formattedContent = content;

  // Add common insurance-related structure if content doesn't already have it
  if (!formattedContent.includes('<h3>') && !formattedContent.includes('<h4>')) {
    // Split content into paragraphs
    const paragraphs = formattedContent.split('\n\n').filter(p => p.trim());
    
    if (paragraphs.length > 1) {
      // Try to identify sections and add structure
      let structuredContent = '';
      
      paragraphs.forEach((paragraph, index) => {
        const trimmed = paragraph.trim();
        
        // Check for potential section headers and organize content
        if (trimmed.toLowerCase().includes('types of') || 
            trimmed.toLowerCase().includes('filing steps') ||
            trimmed.toLowerCase().includes('choosing') ||
            trimmed.toLowerCase().includes('recommendations') ||
            trimmed.toLowerCase().includes('example') ||
            trimmed.toLowerCase().includes('why') ||
            trimmed.toLowerCase().includes('general') ||
            trimmed.toLowerCase().includes('introduction')) {
          structuredContent += `<h3>${trimmed}</h3>`;
        } else if (trimmed.toLowerCase().includes('assets') ||
                   trimmed.toLowerCase().includes('driving') ||
                   trimmed.toLowerCase().includes('remember') ||
                   trimmed.toLowerCase().includes('minimum') ||
                   trimmed.toLowerCase().includes('coverage') ||
                   trimmed.toLowerCase().includes('limits')) {
          structuredContent += `<h4>${trimmed}</h4>`;
        } else {
          // Check if paragraph contains bullet points or lists
          if (trimmed.includes('•') || trimmed.includes('-') || trimmed.includes('*')) {
            const listItems = trimmed.split(/[•\-*]/).filter(item => item.trim());
            if (listItems.length > 1) {
              structuredContent += '<ul>';
              listItems.forEach(item => {
                if (item.trim()) {
                  // Add emphasis to important terms in list items
                  const emphasized = item.trim()
                    .replace(/\b(\$[\d,]+)\b/g, '<strong>$1</strong>')
                    .replace(/\b(minimum|recommended|coverage|limits|liability|at least)\b/gi, '<strong>$1</strong>');
                  structuredContent += `<li>${emphasized}</li>`;
                }
              });
              structuredContent += '</ul>';
            } else {
              structuredContent += `<p>${trimmed}</p>`;
            }
          } else {
            // Add emphasis to important terms and wrap in appropriate boxes
            const emphasized = trimmed
              .replace(/\b(\$[\d,]+)\b/g, '<strong>$1</strong>')
              .replace(/\b(minimum|recommended|coverage|limits|liability|at least)\b/gi, '<strong>$1</strong>');
            
            // Check if this is a recommendation or example
            if (trimmed.toLowerCase().includes('recommend') || 
                trimmed.toLowerCase().includes('expert') ||
                trimmed.toLowerCase().includes('at least')) {
              structuredContent += `<div class="recommendation-box"><p>${emphasized}</p></div>`;
            } else if (trimmed.toLowerCase().includes('example') ||
                       trimmed.toLowerCase().includes('john') ||
                       trimmed.toLowerCase().includes('sarah')) {
              structuredContent += `<div class="example-box"><p>${emphasized}</p></div>`;
            } else if (trimmed.toLowerCase().includes('warning') ||
                       trimmed.toLowerCase().includes('important') ||
                       trimmed.toLowerCase().includes('remember')) {
              structuredContent += `<div class="warning-box"><p>${emphasized}</p></div>`;
            } else {
              structuredContent += `<p>${emphasized}</p>`;
            }
          }
        }
      });
      
      formattedContent = structuredContent;
    }
  }

  return styles + `<div class="content-section">${formattedContent}</div>`;
}; 